import { getAuth } from 'firebase/auth';

export interface CurrentUserInfo {
  email: string;
  name: string;
  role: 'admin' | 'staff' | 'manager' | 'cashier';
  isAdmin: boolean;
}

/**
 * Get the current POS user's info from Firebase Auth + localStorage (saheeh_pos_user).
 * This replaces the broken localStorage.getItem('currentUser') pattern.
 */
export function getCurrentUser(): CurrentUserInfo {
  // 1. Get email from Firebase Auth (most reliable)
  const currentAuth = getAuth();
  const authEmail = currentAuth.currentUser?.email || '';

  // 2. Get profile from localStorage (set during login)
  let storedRole: string = '';
  let storedName: string = '';
  let storedEmail: string = '';
  try {
    const raw = localStorage.getItem('saheeh_pos_user');
    if (raw) {
      const profile = JSON.parse(raw);
      storedRole = profile.role || '';
      storedName = profile.name || '';
      storedEmail = profile.email || '';
    }
  } catch {
    // ignore parse errors
  }

  const email = authEmail || storedEmail;

  // Hardcoded admins always get full admin access regardless of Firestore role
  const HARDCODED_ADMINS = ['admin@saheehpos.com', 'admin@saheehpos.com'];
  if (HARDCODED_ADMINS.includes(email.toLowerCase())) {
    return { email, name: storedName || email, role: 'admin', isAdmin: true };
  }

  // Determine role: stored profile > infer from email > default staff
  let role: CurrentUserInfo['role'] = 'staff';
  if (storedRole && storedRole !== '') {
    role = storedRole as CurrentUserInfo['role'];
  } else if (email) {
    // Infer from email as fallback when localStorage profile is missing/stale
    if (email.includes('admin')) role = 'admin';
    else if (email.includes('manager')) role = 'manager';
    else if (email.includes('cashier')) role = 'cashier';
  }

  const isAdmin = role === 'admin' || role === 'manager';

  return { email, name: storedName || email, role, isAdmin };
}

/**
 * Get display label for current user (e.g. "admin@saheehpos.com (Admin)")
 */
export function getCurrentUserLabel(): string {
  const { email, role } = getCurrentUser();
  const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
  return `${email || 'Unknown'} (${roleLabel})`;
}
